<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, useSlots, watch } from 'vue';
import { COLORS, COLOR_CLASSES } from '@/constants/colors';

interface Props {
  isOpen?: boolean;
  title?: string;
  width?: string;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: true,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const closeModal = () => {
  emit('close');
};

// props 사용 예시
const modalStyle = computed(() => ({
  width: props.width || '500px', // 기본값 설정
  maxWidth: '90vw', // 반응형을 위한 최대 너비
  borderColor: COLORS.border.light,
}));

const showHeader = computed(() => Boolean(props.title));
const slots = useSlots();
const containerRef = ref<HTMLElement | null>(null);

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    e.stopPropagation();
    closeModal();
  }
};

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
});

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      // 포커스 이동으로 접근성 향상
      requestAnimationFrame(() => {
        containerRef.value?.focus();
      });
    }
  },
);
</script>

<template>
  <!-- <Teleport to="body"> -->
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center w-full h-full"
      role="dialog"
      aria-modal="true"
      :aria-label="props.ariaLabel || props.title || '모달'"
    >
      <!-- 배경 오버레이 -->
      <div class="fixed inset-0 bg-[rgba(0,0,0,0.6)] w-full h-full" @click.stop="closeModal" />

      <!-- 오버레이 (viewport 전체, 수평수직 중앙) - slot 내용 있을 때만 -->
      <div
        v-if="slots.overlay?.()?.length"
        class="fixed inset-0 z-[10000] flex items-center justify-center"
      >
        <slot name="overlay" />
      </div>

      <!-- 모달 컨텐츠 -->
      <div
        class="relative bg-white shadow-xl pt-6 pb-9 px-4 flex flex-col outline-none max-h-[90vh]"
        :style="modalStyle"
        tabindex="-1"
        ref="containerRef"
      >
        <!-- 헤더 -->
        <div v-if="showHeader" class="flex items-center justify-between py-2 flex-shrink-0">
          <h3 class="text-[20px] font-bold" :class="[COLOR_CLASSES.text.orange]">
            {{ title }}
          </h3>
          <VIcon
            icon="ri-close-line"
            class="text-[20px] cursor-pointer text-[#404040] font-[700]"
            size="18px"
            @click="closeModal"
          />
        </div>

        <!-- 컨텐츠 (스크롤 가능 영역) -->
        <div class="flex-1 min-h-0 overflow-y-auto mb-4">
          <slot />
        </div>

        <!-- 푸터 (고정) -->
        <div class="flex justify-end gap-2 pt-4 pb-0 flex-shrink-0">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Transition>
  <!-- </Teleport> -->
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
